import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';

interface WidgetProps {
  title: string;
  /** Subtitle shown to the right of the title — usually a count or
   * action verb. */
  subtitle?: string;
  /** Tailwind class for the column span on lg breakpoints. Most
   * widgets fit in 1 column; a few (recent activity, distributor mix)
   * read better at 2. */
  span?: 'lg:col-span-1' | 'lg:col-span-2' | 'lg:col-span-3';
  /** Optional link rendered top-right (typically a deep-link to the
   * filtered parts page or admin queue). */
  actionHref?: string;
  actionLabel?: string;
  /** Set when the data fetch is in flight. Body is replaced with a
   * neutral placeholder so the layout doesn't jump. */
  loading?: boolean;
  /** Set when the data fetch errored — body shows the message. */
  error?: Error | null;
  children: ReactNode;
}

export function Widget({
  title,
  subtitle,
  span = 'lg:col-span-1',
  actionHref,
  actionLabel,
  loading,
  error,
  children,
}: WidgetProps) {
  return (
    <section
      className={cn(
        'flex flex-col rounded-lg border border-stone-200 dark:border-stone-800',
        'bg-white dark:bg-stone-900',
        'col-span-1 md:col-span-2',
        span,
      )}
    >
      <header className="flex items-center justify-between border-b border-stone-100 dark:border-stone-800 px-3 py-2">
        <h3 className="flex items-baseline gap-2">
          <span className="text-sm font-semibold tracking-tight">{title}</span>
          {subtitle && (
            <span className="text-xs text-stone-500 dark:text-stone-400">
              {subtitle}
            </span>
          )}
        </h3>
        {actionHref && actionLabel && (
          <Link
            to={actionHref}
            className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
          >
            {actionLabel}
          </Link>
        )}
      </header>
      <div className="min-h-[3rem] flex-1 p-3 text-sm">
        {loading && (
          <p className="text-stone-400 dark:text-stone-500">Loading…</p>
        )}
        {!loading && error && (
          <p className="text-red-600">{error.message}</p>
        )}
        {!loading && !error && children}
      </div>
    </section>
  );
}

/** Reusable little component for "X of Y" + colored bar. Used by a
 * handful of count-by-label widgets. */
export function BarRow({
  label,
  count,
  total,
  href,
}: {
  label: string;
  count: number;
  total: number;
  href?: string;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-xs">
        {href ? (
          <Link
            to={href}
            className="truncate text-stone-700 dark:text-stone-300 hover:underline"
          >
            {label}
          </Link>
        ) : (
          <span className="truncate text-stone-700 dark:text-stone-300">
            {label}
          </span>
        )}
        <span className="ml-2 shrink-0 font-mono text-stone-500 dark:text-stone-400">
          {count}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100 dark:bg-stone-800">
        <div
          className="h-full rounded-full bg-forge-500"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

/** Capped list of part-row links. Shows "+N more" when truncated. */
export function PartRowList({
  rows,
  emptyText,
  hrefBuilder,
  cap = 8,
  total,
}: {
  rows: Array<{
    part_id: number;
    part_number: string;
    description: string | null;
    mfg_pn?: string | null;
  }>;
  emptyText: string;
  hrefBuilder?: (partNumber: string, partId: number) => string;
  cap?: number;
  total?: number;
}) {
  if (rows.length === 0) {
    return <p className="text-stone-500 dark:text-stone-400">{emptyText}</p>;
  }
  const visible = rows.slice(0, cap);
  const totalCount = total ?? rows.length;
  return (
    <ul className="space-y-1">
      {visible.map((r) => (
        <li key={r.part_id} className="truncate">
          {hrefBuilder ? (
            <Link
              to={hrefBuilder(r.part_number, r.part_id)}
              className="hover:underline"
            >
              <span className="font-mono text-forge-700 dark:text-forge-400">
                {r.part_number}
              </span>
              {r.description && (
                <span className="ml-2 text-stone-600 dark:text-stone-400">
                  {r.description}
                </span>
              )}
            </Link>
          ) : (
            <>
              <span className="font-mono">{r.part_number}</span>
              {r.description && (
                <span className="ml-2 text-stone-600 dark:text-stone-400">
                  {r.description}
                </span>
              )}
            </>
          )}
        </li>
      ))}
      {totalCount > visible.length && (
        <li className="text-xs text-stone-500 dark:text-stone-400">
          + {totalCount - visible.length} more
        </li>
      )}
    </ul>
  );
}

/** Format integer cents → "$X.XX" or "$1,234". Returns "—" for 0
 * to keep empty cells quiet. */
export function formatDollars(cents: number): string {
  if (!cents) return '—';
  const dollars = cents / 100;
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: dollars >= 1000 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}
